import React from 'react';
import './Step5Summary.css';

const Step5Summary = ({ formData, nextStep, prevStep }) => {
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(value || 0);
    };

    const personalInfo = formData.personalInfo || {};
    const isSalary = personalInfo.incomeType === 'salary';
    const isCouple = personalInfo.familyType === 'couple';

    const salaryIncome = formData.salaryIncome || {};
    const salaryExpenses = formData.salaryExpenses || {};
    const salaryInvestment = formData.salaryInvestment || {};
    const jointSalary = formData.jointSalary || {};
    const businessDetails = formData.businessDetails || {};
    const businessExpenses = formData.businessExpenses || {};
    const businessInvestment = formData.businessInvestment || {};
    const landGold = formData.landGold || {};

    const hasValue = (val) => val && val !== '' && parseFloat(val) > 0;

    // --- CALCULATE QUICK METRICS ---
    const calculateTotalIncome = () => {
        if (isCouple) {
            const b = jointSalary.sectionB || {};
            return (parseFloat(b.husbandSalary) || 0) + (parseFloat(b.husbandOtherIncome) || 0) +
                   (parseFloat(b.wifeSalary) || 0) + (parseFloat(b.wifeOtherIncome) || 0);
        }
        return isSalary
            ? (parseFloat(personalInfo.monthlySalary) || 0) + (parseFloat(personalInfo.otherIncomeAmount) || 0)
            : (parseFloat(personalInfo.monthlyBusinessProfit) || 0);
    };

    const totalIncome = calculateTotalIncome();

    const calcTotalExpenses = () => {
        if (isCouple) {
            const c = jointSalary.sectionC || {};
            return Object.values(c).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
        }
        let total = 0;
        const allData = isSalary
            ? { ...salaryIncome, ...salaryExpenses, ...salaryInvestment }
            : { ...businessDetails, ...businessExpenses, ...businessInvestment };

        Object.entries(allData).forEach(([key, val]) => {
            const exclude = ['grossTurnover', 'totalProfit', 'homeLoanOutstanding', 'carLoanOutstanding', 'personalLoanOutstanding', 'otherLoanOutstanding', 'landValue', 'goldValue', 'sipAmount', 'ppfAmount', 'fdRdAmount'];
            if (!exclude.includes(key) && !key.includes('Frequency') && !key.includes('has')) {
                total += parseFloat(val) || 0;
            }
        });
        return total;
    };

    const totalExpenses = calcTotalExpenses();
    const surplus = totalIncome - totalExpenses;
    const savingsRate = totalIncome > 0 ? ((surplus / totalIncome) * 100).toFixed(1) : 0;
    const expenseRatio = totalIncome > 0 ? ((totalExpenses / totalIncome) * 100).toFixed(1) : 0;

    const fieldLabels = {
        // Salary Fixed
        rentEMI: "Rent / Home EMI",
        homeLoanEMI: "Home Loan EMI",
        carLoanEMI: "Car Loan EMI",
        personalLoanEMI: "Personal Loan EMI",
        educationLoanEMI: "Education Loan EMI",
        creditCardEMI: "Credit Card EMI",
        otherLoanEMI: "Other Loan EMI",
        lifeInsurance: "Life Insurance Premium",
        healthInsurance: "Health Insurance Premium",
        criticalIllness: "Critical Illness Premium",
        motorInsurance: "Motor Insurance Premium",
        homeInsurance: "Home Insurance Premium",
        epf: "EPF Contribution",
        vpf: "VPF Contribution",
        ppf: "PPF Contribution",
        nps_80ccd2: "NPS (80CCD2)",
        sukanyaSamriddhi: "Sukanya Samriddhi",
        electricity: "Electricity Bill",
        water: "Water Bill",
        gas: "Gas Bill",
        internet: "Internet Bill",
        broadband: "Broadband Bill",
        ottSubscriptions: "OTT Subscriptions",
        cable: "Cable Bill",
        societyMaintenance: "Society Maintenance",
        propertyTax: "Property Tax",

        // Salary Living
        groceries: "Groceries",
        vegetables: "Vegetables & Fruits",
        dairyProducts: "Dairy Products",
        householdSupplies: "Household Supplies",
        fuelDiesel: "Fuel (Petrol/Diesel)",
        publicTransport: "Public Transport",
        taxiCabUber: "Taxi/Cab (Uber/Ola)",
        vehicleMaintenance: "Vehicle Maintenance",
        parkingCharges: "Parking Charges",
        tollCharges: "Toll Charges",
        doctorConsultationsAmount: "Doctor Consultations",
        medicinesAmount: "Medicines",
        diagnosticTestsAmount: "Diagnostic Tests",
        dentalCareAmount: "Dental Care",
        eyeCareAmount: "Eye Care",
        schoolFeesAmount: "School/College Fees",
        booksStationeryAmount: "Books & Stationery",
        tuitionClassesAmount: "Tuition/Coaching",
        extracurricularAmount: "Extracurricular Activities",
        salonBarber: "Salon / Barber",
        cosmeticsAmount: "Cosmetics & Toiletries",
        clothingFootwearAmount: "Clothing & Footwear",

        // Salary Lifestyle & Investment
        entertainment: "Entertainment",
        diningOut: "Dining Out",
        moviesTheater: "Movies & Theater",
        sportsHobbies: "Sports & Hobbies",
        clubMemberships: "Club Memberships",
        shopping: "Shopping",
        electronics: "Electronics",
        furnitureDecor: "Furniture & Decor",
        apparelAccessories: "Apparel & Accessories",
        giftsDonations: "Gifts & Donations",
        travel: "Travel",
        vacationPlanning: "Vacation Planning",
        dailyOfficeCommute: "Daily Office Commute",
        parentingSupport: "Parenting Support",
        childrenAllowance: "Children Allowance",
        domesticHelp: "Domestic Help",
        weddingContributions: "Wedding Contributions",
        festivalExpenses: "Festival Expenses",
        religiousDonations: "Religious Donations",
        charityNGO: "Charity/NGO",
        birthdayCelebrations: "Birthday Celebrations",
        anniversaryCelebrations: "Anniversary Celebrations",
        familyFunctions: "Family Functions",
        professionalCourses: "Professional Courses",
        certificationFees: "Certification Fees",
        booksJournals: "Books & Journals",
        workFromHome: "Work From Home Setup",
        professionalMembership: "Professional Membership",
        incomeTaxAdvance: "Income Tax (Advance)",
        tdsInterestIncome: "TDS on Interest",
        capitalGainsTax: "Capital Gains Tax",
        elss: "ELSS (80C) Investments",
        mutualFunds: "Mutual Funds SIP",
        stocksEquity: "Stocks/Equity",
        recurringDeposits: "Recurring Deposits",
        fixedDeposits: "Fixed Deposits",
        goldBonds: "Gold Bonds (SGB)",
        realEstate: "Real Estate Investments",
        liquidFund: "Liquid / Cash Funds",
        emergencyFund: "Emergency Fund",

        // Business
        grossTurnover: "Annual Turnover",
        totalProfit: "Net Profit",
        rawMaterialPurchases: "Raw Materials",
        directLabourWages: "Direct Labour",
        manufacturingOverheads: "Mfg Overheads",
        salariesWages: "Employee Salaries",
        employeeBenefits: "Employee Benefits",
        officeRent: "Office/Factory Rent",
        electricityPower: "Electricity & Power",
        advertisingPromotion: "Marketing/Ads",
        digitalMarketing: "Digital Marketing",
        legalProfessionalFees: "Legal/Audit Fees",
        interestBusinessLoans: "Loan Interest",
        ownerDrawings: "Owner Drawings",
        termInsuranceOwner: "Life Insurance (Owner)",
        healthInsuranceOwner: "Health Insurance (Owner)",
        businessReserveFund: "Business Reserve Fund",
        loanEMIMachinery: "Machinery Loan EMI",

        // Assets
        landValue: "Land / Property",
        goldValue: "Gold / Jewelry",
        sipAmount: "Mutual Fund Value",
        ppfAmount: "PPF Balance",
        fdRdAmount: "FD/RD Balance",
        otherInvestmentAmount: "Other Investments",
        homeLoanOutstanding: "Home Loan Bal.",
        personalLoanOutstanding: "Personal Loan Bal.",
        carLoanOutstanding: "Car Loan Bal.",
        otherLoanOutstanding: "Other Loan Bal.",

        // Joint Form
        husbandSalary: "Husband Salary",
        wifeSalary: "Wife Salary",
        husbandOtherIncome: "Husband Other Income",
        wifeOtherIncome: "Wife Other Income",
        groceries: "Groceries & Utilities",
        transport: "Transport",
        dining: "Dining & Entertainment",
        healthInsurance: "Health Insurance",
        rent: "Rent",
        schoolFees: "School Fees",
        otherExpenses: "Other Expenses",
        husbandEPF: "Husband EPF",
        wifeEPF: "Wife EPF",
        husbandPPF: "Husband PPF",
        wifePPF: "Wife PPF",
        husbandMutualFund: "Husband Mutual Fund",
        wifeMutualFund: "Wife Mutual Fund",
        retirementCorpus: "Retirement Target",
        childEducation: "Child Education Target",
        vacation: "Vacation Target",
        homeValue: "Home Market Value",
        carValue: "Car Value",
        otherAssets: "Other Property/Gold"
    };

    const renderCardSection = (title, data, fields, icon) => {
        const activeFields = fields.filter(f => hasValue(data[f]));
        if (activeFields.length === 0) return null;

        return (
            <div className="summary-section-card" key={title}>
                <h3 className="section-title">
                    <span className="section-icon">{icon}</span>
                    {title}
                </h3>
                <div className="summary-grid">
                    {activeFields.map(field => {
                        const isHusband = field.toLowerCase().includes('husband');
                        const isWife = field.toLowerCase().includes('wife');
                        
                        return (
                            <div className="summary-item" key={field}>
                                <span className="label">
                                    {isHusband && <span style={{ color: '#1d4ed8', marginRight: '4px' }}>♂</span>}
                                    {isWife && <span style={{ color: '#be185d', marginRight: '4px' }}>♀</span>}
                                    {fieldLabels[field] || field}
                                    {field === 'husbandOtherIncome' && data.husbandOtherIncomeType ? ` (${data.husbandOtherIncomeType})` : ''}
                                    {field === 'wifeOtherIncome' && data.wifeOtherIncomeType ? ` (${data.wifeOtherIncomeType})` : ''}
                                    :
                                </span>
                                <span className="value" style={{ 
                                    color: isHusband ? '#1d4ed8' : isWife ? '#be185d' : 'inherit',
                                    fontWeight: (isHusband || isWife) ? '600' : '500'
                                }}>
                                    {formatCurrency(data[field])}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="summary-step">
            <div className="summary-hero">
                <div className="hero-content">
                    <h2 className="hero-title">Financial Health Preview</h2>
                    <p className="hero-subtitle">Based on your entries, here is a quick look at your current standing.</p>
                </div>

                <div className="hero-stats">
                    <div className="stat-card">
                        <span className="summary-stat-label" style={{ color: '#000000', fontWeight: '800' }}>Estimated Savings Rate</span>
                        <div className="stat-value-group">
                            <span className="stat-value" style={{ color: '#000000', fontWeight: '800' }}>{savingsRate}%</span>
                            <div className="progress-bar-bg">
                                <div className="progress-bar-fill" style={{ width: `${Math.min(savingsRate, 100)}%`, background: savingsRate > 20 ? '#069668' : '#f59e0b' }}></div>
                            </div>
                        </div>
                    </div>
                    <div className="stat-card">
                        <span className="summary-stat-label" style={{ color: '#000000', fontWeight: '800' }}>Expense to Income Ratio</span>
                        <div className="stat-value-group">
                            <span className="stat-value" style={{ color: '#000000', fontWeight: '800' }}>{expenseRatio}%</span>
                            <div className="progress-bar-bg">
                                <div className="progress-bar-fill" style={{ width: `${Math.min(expenseRatio, 100)}%`, background: expenseRatio > 70 ? '#ef4444' : '#069668' }}></div>
                            </div>
                        </div>
                    </div>
                    <div className="stat-card highlight" style={{ color: '#000000 !important' }}>
                        <span className="summary-stat-label" style={{ color: '#000000', fontWeight: '800' }}>Monthly Surplus</span>
                        <span className="stat-value" style={{ color: '#000000', fontWeight: '800' }}>{formatCurrency(surplus)}</span>
                    </div>
                </div>
            </div>

            <div className="summary-scroll-container">
                <div className="summary-section-card">
                    <h3 className="section-title"><span className="section-icon">👤</span>Personal Profile</h3>
                    <div className="summary-grid">
                        <div className="summary-item"><span className="label">Name:</span><span className="value">{isCouple ? `${jointSalary.sectionA?.husbandName} & ${jointSalary.sectionA?.wifeName}` : personalInfo.name}</span></div>
                        <div className="summary-item"><span className="label">Type:</span><span className="value" style={{ textTransform: 'capitalize' }}>{isCouple ? 'Couple' : personalInfo.incomeType?.replace('_', ' ')}</span></div>
                        <div className="summary-item"><span className="label">Monthly Income:</span><span className="value">{formatCurrency(totalIncome)}</span></div>
                    </div>
                </div>

                {isCouple ? (
                    <>
                        {renderCardSection("Joint Monthly Income", jointSalary.sectionB, ['husbandSalary', 'husbandOtherIncome', 'wifeSalary', 'wifeOtherIncome'], "💰")}
                        {renderCardSection("Joint Household Expenses", jointSalary.sectionC, ['homeLoanEMI', 'personalLoanEMI', 'carLoanEMI', 'groceries', 'transport', 'dining', 'shopping', 'healthInsurance', 'rent', 'schoolFees', 'otherExpenses'], "🏠")}
                        {renderCardSection("Investments & Savings", { ...jointSalary.sectionD, ...jointSalary.sectionG }, ['husbandEPF', 'wifeEPF', 'husbandPPF', 'wifePPF', 'husbandMutualFund', 'wifeMutualFund', 'emergencyFund'], "📈")}
                        {renderCardSection("Financial Goals", jointSalary.sectionI, ['retirementCorpus', 'childEducation', 'vacation'], "🎯")}
                        {renderCardSection("Existing Assets", jointSalary.sectionJ, ['homeValue', 'carValue', 'otherAssets'], "🏔️")}
                    </>
                ) : isSalary ? (
                    <>
                        {renderCardSection("Fixed Expenses & Debt", salaryIncome, ['rentEMI', 'homeLoanEMI', 'carLoanEMI', 'personalLoanEMI', 'educationLoanEMI', 'creditCardEMI', 'otherLoanEMI', 'electricity', 'water', 'gas', 'internet', 'broadband', 'ottSubscriptions', 'cable', 'societyMaintenance', 'propertyTax'], "🏠")}
                        {renderCardSection("Insurance Premiums", salaryIncome, ['lifeInsurance', 'healthInsurance', 'criticalIllness', 'motorInsurance', 'homeInsurance'], "🛡️")}
                        {renderCardSection("Daily Living", salaryExpenses, ['groceries', 'vegetables', 'dairyProducts', 'householdSupplies', 'fuelDiesel', 'publicTransport', 'taxiCabUber', 'vehicleMaintenance', 'parkingCharges', 'tollCharges'], "🛒")}
                        {renderCardSection("Healthcare", salaryExpenses, ['doctorConsultationsAmount', 'medicinesAmount', 'diagnosticTestsAmount', 'dentalCareAmount', 'eyeCareAmount'], "🏥")}
                        {renderCardSection("Education", salaryExpenses, ['schoolFeesAmount', 'booksStationeryAmount', 'tuitionClassesAmount', 'extracurricularAmount'], "🎓")}
                        {renderCardSection("Personal Care", salaryExpenses, ['salonBarber', 'cosmeticsAmount', 'clothingFootwearAmount'], "🧴")}
                        {renderCardSection("Lifestyle & Recreation", salaryInvestment, ['entertainment', 'diningOut', 'moviesTheater', 'sportsHobbies', 'clubMemberships', 'shopping', 'electronics', 'furnitureDecor', 'apparelAccessories', 'giftsDonations', 'travel', 'vacationPlanning', 'dailyOfficeCommute'], "✨")}
                        {renderCardSection("Family & Social", salaryInvestment, ['parentingSupport', 'childrenAllowance', 'domesticHelp', 'weddingContributions', 'festivalExpenses', 'religiousDonations', 'charityNGO', 'birthdayCelebrations', 'anniversaryCelebrations', 'familyFunctions'], "👪")}
                        {renderCardSection("Savings & Assets", { ...salaryIncome, ...salaryInvestment }, ['epf', 'vpf', 'ppf', 'nps_80ccd2', 'sukanyaSamriddhi', 'elss', 'mutualFunds', 'stocksEquity', 'recurringDeposits', 'fixedDeposits', 'goldBonds', 'realEstate', 'liquidFund', 'emergencyFund', 'otherInvestmentAmount'], "💰")}
                        {renderCardSection("Land & Gold Assets", landGold, ['landValue', 'goldValue'], "🏔️")}
                        {renderCardSection("Investment Assets", landGold, ['sipAmount', 'ppfAmount', 'fdRdAmount'], "📈")}
                        {renderCardSection("Outstanding Liabilities", landGold, ['homeLoanOutstanding', 'personalLoanOutstanding', 'carLoanOutstanding', 'otherLoanOutstanding'], "📑")}
                    </>
                ) : (
                    <>
                        {renderCardSection("Business Performance", businessDetails, ['grossTurnover', 'totalProfit'], "📈")}
                        {renderCardSection("Operating Costs", businessDetails, ['rawMaterialPurchases', 'directLabourWages', 'manufacturingOverheads', 'salariesWages', 'employeeBenefits', 'officeRent', 'factoryRent', 'electricityPower', 'advertisingPromotion', 'digitalMarketing', 'legalProfessionalFees'], "🏢")}
                        {renderCardSection("Statutory & Strategy", { ...businessExpenses, ...businessInvestment }, ['interestBusinessLoans', 'ownerDrawings', 'termInsuranceOwner', 'healthInsuranceOwner', 'businessReserveFund', 'loanEMIMachinery'], "💼")}
                        {renderCardSection("Land & Gold Assets", landGold, ['landValue', 'goldValue'], "🏔️")}
                        {renderCardSection("Investment Assets", landGold, ['sipAmount', 'ppfAmount', 'fdRdAmount'], "📈")}
                        {renderCardSection("Outstanding Liabilities", landGold, ['homeLoanOutstanding', 'personalLoanOutstanding', 'carLoanOutstanding', 'otherLoanOutstanding'], "📑")}
                    </>
                )}
            </div>

            <div className="form-actions">

                <button type="button" onClick={prevStep} className="btn-secondary">Back to Edit</button>
                <button type="button" onClick={() => nextStep(formData)} className="btn-primary">Generate Final Report ₹299 →</button>
            </div>
        </div>
    );
};

export default Step5Summary;
